import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { BILLS_ROUTES } from "@routes/bills"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    BILLS_LIST_CONTROLS_ACTIVE_TAB,
    BILLS_LIST_CONTROLS_KEYWORD_SEARCH,
    BILLS_LIST_CONTROLS_PHASE,
    BILLS_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { BILL_CARDS_VIEW_ATTRIBUTE_OPTIONS, BILL_CARDS_VIEW_SORT_OPTIONS } from "./BillList.constants"
import { BillObjectsViewTabs } from "./BillObjectsViewTabs"
import BillCardsView from "./views/BillCardsView/BillCardsView"
import BillTableView from "./views/BillTableView/BillTableView"

export default function BillList() {
    const isDesktop = useIsDesktop()
    const { hasPermission } = useHasAccess()
    const [createRoute] = useSlugExtractor([BILLS_ROUTES.CREATE])

    return (
        <AppFrame.Root collaborationRoomName="bill-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={BILLS_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle
                                        label={objectMap.bill.labelPlural}
                                        icon={objectMap.bill.iconActive}
                                    />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            bills_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                bills_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Bills" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            bills_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={BILLS_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={BILLS_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={BILLS_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Bills"
                            objectType="bill"
                            objectIndexName="Bill"
                            objectName="Bill"
                            defaultActivePhase="open"
                            objectCreateRoute={createRoute}
                            isRowClickable={hasPermission("bills_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "bills_create_permission",
                                PERMISSION_LEVEL.RESTRICTED,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={BILL_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                    sortOptions={BILL_CARDS_VIEW_SORT_OPTIONS}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="open" title="Open" />
                                                <ObjectsView.Phase phase="closed" title="Closed" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <BillObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <BillTableView />}
                                    {activeViewType === "cards" && <BillCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
